import React from "react";
import SocialIcon from "../components/SocialIcon";
import { socialLinks } from "../config/socialLinks";
import { getSiteContent, interpolate } from "../content/siteContent";
import { useRemotePage } from "../remote/RemoteControlContext";

const accentClasses = {
  sky: "border-sky-300/20 bg-sky-400/10 text-sky-200 hover:border-sky-300/45 hover:bg-sky-400/15",
  emerald: "border-emerald-300/20 bg-emerald-400/10 text-emerald-200 hover:border-emerald-300/45 hover:bg-emerald-400/15",
  pink: "border-pink-300/20 bg-pink-400/10 text-pink-200 hover:border-pink-300/45 hover:bg-pink-400/15",
};

function SocialCard({ social, copy, common, unavailable }) {
  const cardContent = (
    <>
      <div className="flex items-center gap-4">
        <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-slate-950/45 ring-1 ring-white/10">
          <SocialIcon platform={social.id} className="h-7 w-7" />
        </span>
        <div className="text-start">
          <h2 className="text-xl font-black text-white">{copy.label}</h2>
          <p className="mt-1 text-sm leading-6 text-indigo-100/65">{copy.description}</p>
        </div>
      </div>
      <span className="mt-6 inline-flex items-center gap-2 text-sm font-black">
        {social.url ? interpolate(common.openOn, { platform: copy.label }) : common.comingSoon}
        {social.url && <span aria-hidden="true">↗</span>}
      </span>
    </>
  );

  const className = `flex h-full flex-col justify-between rounded-3xl border p-6 text-start shadow-2xl shadow-slate-950/20 transition ${accentClasses[social.accent]}`;

  return social.url ? (
    <a href={social.url} target="_blank" rel="noreferrer" className={className} aria-label={interpolate(common.openOn, { platform: copy.label })}>
      {cardContent}
    </a>
  ) : (
    <div className={`${className} opacity-75`} aria-disabled="true" title={unavailable}>
      {cardContent}
    </div>
  );
}

export default function ContactPage() {
  const content = getSiteContent();
  const { common, contact, direction, footer } = content;
  const availableSocials = socialLinks.filter((social) => social.url);

  useRemotePage({
    id: "contact",
    title: contact.remote.title,
    subtitle: contact.remote.subtitle,
    state: {},
    sections: [{
      title: contact.remote.sectionTitle,
      controls: availableSocials.map((social) => ({
        id: `contact.${social.id}`,
        type: "button",
        label: contact.channels[social.id].label,
      })),
    }],
    actions: Object.fromEntries(
      availableSocials.map((social) => [
        `contact.${social.id}`,
        () => window.open(social.url, "_blank", "noopener,noreferrer"),
      ]),
    ),
  });

  return (
    <main className="relative flex flex-grow items-center overflow-hidden" dir={direction}>
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute left-[10%] top-8 h-72 w-72 rounded-full bg-sky-500/15 blur-3xl" />
        <div className="absolute bottom-4 right-[8%] h-80 w-80 rounded-full bg-pink-500/15 blur-3xl" />
      </div>

      <section className="relative z-10 mx-auto w-full max-w-6xl px-4 py-16 sm:py-24">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-sky-200/80">{contact.eyebrow}</p>
          <h1 className="mt-4 text-4xl font-black text-white sm:text-5xl">{contact.title}</h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-indigo-100/75 sm:text-lg">{contact.description}</p>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {socialLinks.map((social) => (
            <SocialCard
              key={social.id}
              social={social}
              copy={contact.channels[social.id]}
              common={common}
              unavailable={footer.unavailable}
            />
          ))}
        </div>

        <p className="mx-auto mt-10 max-w-2xl rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-center text-sm leading-7 text-indigo-100/65">
          {contact.availability}
        </p>
      </section>
    </main>
  );
}
